import React, { useState } from 'react';

// Mobile detection utility
const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        ('ontouchstart' in window) ||
        (navigator.maxTouchPoints > 0);
};

// Input callback interfaces
interface MobileControlsProps {
    onLeftStickMove?: (x: number, y: number) => void;
    onRightStickMove?: (deltaX: number, deltaY: number) => void;
    onJumpPress?: () => void;
    // Sprint button callbacks (hold to sprint)
    onSprintStart?: () => void;
    onSprintEnd?: () => void;
}

// Mobile Controls Component
const MobileControls = ({
    onLeftStickMove,
    onRightStickMove,
    onJumpPress,
    onSprintStart,
    onSprintEnd
}: MobileControlsProps) => {
    const [joystickPosition, setJoystickPosition] = useState({ x: 0, y: 0 });
    const [joystickCenter, setJoystickCenter] = useState({ x: 0, y: 0 });
    const [isJoystickActive, setIsJoystickActive] = useState(false);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [lastCameraTouch, setLastCameraTouch] = useState({ x: 0, y: 0 });

    // Track active touches by their identifier
    const [activeTouches, setActiveTouches] = useState(new Map());

    const handleTouchStart = (event: any) => {
        // Handle all new touches
        for (let i = 0; i < event.changedTouches.length; i++) {
            const touch = event.changedTouches[i];
            const screenWidth = window.innerWidth;
            const isLeftHalf = touch.clientX < screenWidth / 2;

            // Store touch info in our map
            setActiveTouches(prev => {
                const newMap = new Map(prev);
                newMap.set(touch.identifier, {
                    isLeft: isLeftHalf,
                    startX: touch.clientX,
                    startY: touch.clientY,
                    lastX: touch.clientX,
                    lastY: touch.clientY
                });
                return newMap;
            });

            if (isLeftHalf && !isJoystickActive) {
                // Left half - create joystick at touch position
                setJoystickCenter({ x: touch.clientX, y: touch.clientY });
                setJoystickPosition({ x: 0, y: 0 });
                setIsJoystickActive(true);
            } else if (!isLeftHalf && !isCameraActive) {
                // Right half - start camera control
                setLastCameraTouch({ x: touch.clientX, y: touch.clientY });
                setIsCameraActive(true);
            }
        }
    };

    const handleTouchMove = (event: any) => {
        // Handle all moving touches
        for (let i = 0; i < event.changedTouches.length; i++) {
            const touch = event.changedTouches[i];
            const touchInfo = activeTouches.get(touch.identifier);

            if (!touchInfo) continue; // Touch not in our map

            if (touchInfo.isLeft && isJoystickActive) {
                // Handle joystick movement
                const deltaX = touch.clientX - joystickCenter.x;
                const deltaY = touch.clientY - joystickCenter.y;
                const maxDistance = 50; // Maximum joystick radius

                const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                const limitedDistance = Math.min(distance, maxDistance);
                const angle = Math.atan2(deltaY, deltaX);

                const x = Math.cos(angle) * limitedDistance;
                const y = Math.sin(angle) * limitedDistance;

                setJoystickPosition({ x, y });

                // Normalize to -1 to 1 range for game input
                const normalizedX = x / maxDistance;
                const normalizedY = y / maxDistance;
                onLeftStickMove?.(normalizedX, normalizedY);
            } else if (!touchInfo.isLeft && isCameraActive) {
                // Handle camera movement
                const deltaX = touch.clientX - touchInfo.lastX;
                const deltaY = touch.clientY - touchInfo.lastY;

                onRightStickMove?.(deltaX, deltaY);

                // Update last position for this touch
                setActiveTouches(prev => {
                    const newMap = new Map(prev);
                    const updated = { ...touchInfo, lastX: touch.clientX, lastY: touch.clientY };
                    newMap.set(touch.identifier, updated);
                    return newMap;
                });
            }
        }
    };

    const handleTouchEnd = (event: any) => {
        event.preventDefault();

        // Handle all ended touches
        for (let i = 0; i < event.changedTouches.length; i++) {
            const touch = event.changedTouches[i];
            const touchInfo = activeTouches.get(touch.identifier);

            if (!touchInfo) continue;

            // Remove touch from our map
            setActiveTouches(prev => {
                const newMap = new Map(prev);
                newMap.delete(touch.identifier);
                return newMap;
            });

            if (touchInfo.isLeft && isJoystickActive) {
                setIsJoystickActive(false);
                setJoystickPosition({ x: 0, y: 0 });
                onLeftStickMove?.(0, 0);
            } else if (!touchInfo.isLeft && isCameraActive) {
                setIsCameraActive(false);
            }
        }
    };

    const handleJumpButton = (event: any) => {
        event.stopPropagation();
        onJumpPress?.();
    };

    const handleSprintStart = (event: any) => {
        event.stopPropagation();
        onSprintStart?.();
    };

    const handleSprintEnd = (event: any) => {
        event.stopPropagation();
        onSprintEnd?.();
    };

    if (!isMobile()) {
        return null;
    }

    return (
        <div className="mobile-controls">
            {/* Full screen touch area */}
            <div
                className="touch-area"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            />

            {/* Touch hint circle - shown when joystick is not active */}
            {!isJoystickActive && (
                <div className="joystick-hint">
                </div>
            )}

            {/* Dynamic joystick - only visible when active */}
            {isJoystickActive && (
                <div
                    className="joystick-container dynamic"
                    style={{
                        left: joystickCenter.x - 60, // Center the 120px container
                        top: joystickCenter.y - 60
                    }}
                >
                    <div className="joystick-base">
                        <div
                            className="joystick-knob"
                            style={{
                                transform: `translate(${joystickPosition.x}px, ${joystickPosition.y}px)`
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="action-buttons">
                <div
                    className="jump-button"
                    onTouchStart={handleJumpButton}
                >
                    JUMP
                </div>
                <div
                    className="sprint-button"
                    onTouchStart={handleSprintStart}
                    onTouchEnd={handleSprintEnd}
                    onTouchCancel={handleSprintEnd}
                >
                    SPRINT
                </div>
            </div>
        </div>
    );
};

export default MobileControls;
